import { isEmpty, isNil } from 'lodash';
import { RETURN_CHANNEL_SLUG_QUERY } from '@shared/scenes/cards/form/update/constants';
import { RouteParamMapping, WebPathMapping } from './routeParams';

function prepareJSONparams(object: Record<string, any>) {
  return JSON.stringify(object);
}

export const browserRouteMapping: Partial<WebPathMapping<RouteParamMapping>> = {
  Home: () => `/`,
  CreateAdditionalBusiness: () => `/newbusiness`,
  BaseChannelsTab: () => `/channels`,
  Channel: ({ channelSlug, selectedMessage, unreadSince }) => {
    if (!selectedMessage) {
      return `/${channelSlug}`;
    }
    const path = `/${channelSlug}/messages/${selectedMessage.id}/${selectedMessage.createdAt}`;
    return unreadSince ? `${path}?unreadSince=${encodeURIComponent(unreadSince)}` : path;
  },
  ChannelInfos: ({ channelSlug }) => `/${channelSlug}/cards/infos`,
  ChannelReports: ({ channelSlug }) => `/${channelSlug}/reports`,
  AdvancedChannelOptions: ({ channelSlug }) => `/${channelSlug}/cards/infos/advanced`,
  ChannelEdit: ({ channelSlug, autoFocus }) => {
    let path = `/${channelSlug}/cards/infos/edit`;
    if (autoFocus) {
      path += `?autoFocus=${autoFocus}`;
    }
    return path;
  },
  WorkspaceTrash: ({ channelSlug }) => `/${channelSlug}/cards/trash`,
  ChannelUserList: ({ channelSlug }) => `/${channelSlug}/members`,
  ChannelNotificationSettings: ({ channelSlug }) => `/${channelSlug}/notifications`,
  ChannelAddPeople: ({ channelSlug }) => `/${channelSlug}/add`,
  ChannelCreateShareLink: ({ channelSlug }) => `/${channelSlug}/cards/infos/share-link`,
  ChannelCardList: ({ channelSlug, templateSlug }) =>
    templateSlug ? `/${channelSlug}/cards/t/${templateSlug}` : `/${channelSlug}`,
  WorkspaceSectionAddPeople: ({ workspaceSectionId }) => `/section/${workspaceSectionId}/add`,
  CardCreate: ({ channelSlug, fieldOverrides, restoreSavedData, fromEntity }) => {
    let path = `/${channelSlug}/cards/create`;
    const urlParams: Record<string, string> = {};
    if (!isNil(fieldOverrides)) {
      urlParams['fields'] = prepareJSONparams(fieldOverrides);
    }
    if (!isNil(fromEntity)) {
      urlParams['fromEntity'] = fromEntity;
    }
    if (!isNil(restoreSavedData)) {
      urlParams['restoreSavedData'] = 'true';
    }

    if (!isEmpty(urlParams)) {
      const query = new URLSearchParams(urlParams);
      path += `/?${query.toString()}`;
    }
    return path;
  },
  // The create form's own child route, so the query params below are the form's rather than this
  // route's: passed through as they came off the URL, they keep the form underneath the one the user
  // was already looking at instead of re-seeding it.
  CardCreateCapture: ({ channelSlug, fields, fromEntity, restoreSavedData }) => {
    let path = `/${channelSlug}/cards/create/capture`;
    const urlParams: Record<string, string> = {};
    if (!isNil(fields)) {
      urlParams['fields'] = fields;
    }
    if (!isNil(fromEntity)) {
      urlParams['fromEntity'] = fromEntity;
    }
    if (restoreSavedData === true) {
      urlParams['restoreSavedData'] = 'true';
    }

    if (!isEmpty(urlParams)) {
      const query = new URLSearchParams(urlParams);
      path += `/?${query.toString()}`;
    }
    return path;
  },
  CardHistory: ({ cardId, channelSlug }) => `/${channelSlug}/cards/${cardId}/history`,
  // The update form's own child route, so the query params below are the form's rather than this
  // route's: passed through as they came off the URL, they keep the form underneath the one the user
  // was already looking at instead of re-seeding it.
  CardUpdateCapture: ({
    cardId,
    channelSlug,
    templateSlug,
    fields,
    restoreSavedData,
    returnChannelSlug,
    returnCardId,
  }) => {
    // Under whichever update path the form is on, because Capture has to be a child of the form it
    // opened from — `/update` and `/update/t/:slug` are two different routes mounting the same form.
    let path = !!templateSlug
      ? `/${channelSlug}/cards/${cardId}/update/t/${templateSlug}/capture`
      : `/${channelSlug}/cards/${cardId}/update/capture`;
    const urlParams: Record<string, string> = {};
    if (!isNil(fields)) {
      urlParams['fields'] = fields;
    }
    if (restoreSavedData === true) {
      urlParams['restoreSavedData'] = 'true';
    }
    if (!isNil(returnChannelSlug)) {
      urlParams[RETURN_CHANNEL_SLUG_QUERY] = returnChannelSlug;
    }
    if (!isNil(returnCardId)) {
      urlParams['returnCardId'] = returnCardId;
    }

    if (!isEmpty(urlParams)) {
      const query = new URLSearchParams(urlParams);
      path += `/?${query.toString()}`;
    }
    return path;
  },
  CardUpdate: ({
    cardId,
    channelSlug,
    templateSlug,
    fieldOverrides,
    restoreSavedData,
    returnChannelSlug,
    returnCardId,
  }) => {
    let path = `/${channelSlug}/cards/${cardId}/update`;
    if (!!templateSlug) {
      path += `/t/${templateSlug}`;
    }
    const urlParams: Record<string, string> = {};
    if (!isNil(fieldOverrides)) {
      urlParams['fields'] = prepareJSONparams(fieldOverrides);
    }
    if (!isNil(restoreSavedData)) {
      urlParams['restoreSavedData'] = 'true';
    }
    if (!isNil(returnChannelSlug)) {
      urlParams[RETURN_CHANNEL_SLUG_QUERY] = returnChannelSlug;
    }
    if (!isNil(returnCardId)) {
      urlParams['returnCardId'] = returnCardId;
    }

    if (!isEmpty(urlParams)) {
      const query = new URLSearchParams(urlParams);
      path += `/?${query.toString()}`;
    }

    return path;
  },
  CardBulkUpdate: ({
    channelSlug,
    cardId,
    workflowTemplateId,
    recurringEntitiesScheduleId,
    allOrUpcoming,
  }) => {
    return `/${channelSlug}/cards/${cardId}/update/${workflowTemplateId}/bulk/${recurringEntitiesScheduleId}/${allOrUpcoming}`;
  },
  NewMessageContainer: () => `/channel/create`,
  MessageStart: () => `/channel/create`,
  MessageGroupCreate: () => `/channel/create/new`,
  MessageGroupName: () => `/channel/create/details`,
  CreateChannelInviteContacts: () => `/channel/create/new`,
  WorkflowLibrary: () => `/workflows`,
  WorkflowLibraryDetail: ({ id }: { id: string }) => `/workflows/${id}`,
  // Bundle channel creation routes (formerly app listing)
  BundleInstallSetup: ({ bundleId }) => `/bundle/install/${bundleId}`,
  BundleInstallAddPeople: ({ bundleId }) => `/bundle/install/${bundleId}/people`,
  BundleInstallDetails: ({ bundleId }) => `/bundle/install/${bundleId}/details`,
  // Duplicate workspace routes
  DuplicateWorkspace: ({ channelSlug }) => `/${channelSlug}/duplicate`,
  DuplicateWorkspacePeople: ({ channelSlug }) => `/${channelSlug}/duplicate`,
  DuplicateWorkspaceDetails: ({ channelSlug }) => `/${channelSlug}/duplicate/details`,
  // Duplicate workspace section routes
  DuplicateWorkspaceSection: ({ workspaceSectionId }) => `/section/${workspaceSectionId}/duplicate`,
  DuplicateWorkspaceSectionPeople: ({ workspaceSectionId }) =>
    `/section/${workspaceSectionId}/duplicate`,
  DuplicateWorkspaceSectionDetails: ({ workspaceSectionId }) =>
    `/section/${workspaceSectionId}/duplicate/details`,
  Login: () => `/signup`,
  LoginExistingAccount: ({ error } = {}) => (isNil(error) ? '/signin' : `/signin?error=${error}`),
  LoginVerificationCode: () => `/verify`,
  LoginBusiness: () => `/signin/biz`,
  LoginBusinessUserList: () => `/auth`,
  LoginSamlAuthentication: ({ code }) => `/saml-auth?code=${code}`,
  OnboardingProfile: () => `/onboarding/profile`,
  OnboardingCreateOrJoinBusiness: () => `/onboarding/createOrJoin`,
  OnboardingCreateBusiness: () => `/onboarding/business`,
  OnboardingSelectWorkflows: () => `/onboarding/workflows`,
  OnboardingCreateDirectMessages: () => `/onboarding/dms`,
  OnboardingLastStep: () => `/onboarding/lastStep`,
  OnboardingInviteContacts: () => `/onboarding/invite`,
  OnboardingDone: () => `/onboarding/done`,
  OnboardingBundleInstall: ({ bundleId }) => `/onboarding/bundle/${bundleId}`,
  DashboardTab: () => '/dashboard',
  ReportingTab: () => '/reports',
  AskTab: () => '/ask',
  AskThread: ({ threadId }) => `/ask/${threadId}`,
  AskCard: ({ threadId, channelSlug, cardId }) => `/ask/${threadId}/cards/${channelSlug}/${cardId}`,
  Report: ({ workflowTemplateId, reportId }) => `/reports/${workflowTemplateId}/${reportId}`,
  Playground: ({ tab }) => {
    if (isNil(tab)) {
      return '/playground';
    } else {
      return `/playground/${tab}`;
    }
  },
};

export const megaModalRouteMapping: Partial<WebPathMapping<RouteParamMapping>> = {
  ProfileEdit: () => '/profile',
  Preferences: () => '/preferences',
  NotificationSettings: () => '/notifications',
  Bookmarks: () => '/bookmarks',
  Billing: () => '/billing',
  BusinessEdit: () => '/business',
  BusinessSettings: () => '/settings',
  BulkUpdateQueue: () => '/bulk-update-queue',
  Trash: () => '/trash',
  Directory: () => '/directory',
  DirectoryProfile: ({ userId }) => `/directory/user/${Number(userId)}`,
  UserGroups: () => '/user-groups',
  UserGroupsProfile: ({ groupId }) => `/user-groups/group/${groupId}`,
  InviteContacts: () => '/invite',
};
